import { useEffect, useState } from "react";
import { getBaseURL } from "../../services/api/core";
import * as Sentry from "@sentry/react";
import { FEATURE_FLAGS } from "../../config";

export default function ErrorTest() {
  document.title = "R&S Sentry Test Page";

  const [baseURL, setBaseURL] = useState("");
  const [lastTestResult, setLastTestResult] = useState("");

  useEffect(() => {
    getBaseURL().then((url) => setBaseURL(url));
  }, []);

  const handleCapturedError = () => {
    const errorMsg = `Sentry Test: Captured Exception at ${new Date().toLocaleTimeString()} from ${baseURL.baseURL || "unknown"}`;
    Sentry.captureException(new Error(errorMsg));
    setLastTestResult(`Captured error sent to Sentry: "${errorMsg}"`);
  };

  const handleUncaughtError = () => {
    setLastTestResult("⚠️ About to throw uncaught error (will trigger ErrorBoundary)...");
    // Delay to let the user see the message
    setTimeout(() => {
      throw new Error(`Sentry Test: Uncaught Error at ${new Date().toLocaleTimeString()}`);
    }, 100);
  };

  const containerStyle = {
    maxWidth: "800px",
    margin: "40px auto",
    padding: "30px",
    fontFamily: "Inter, system-ui, sans-serif",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  const statusBoxStyle = {
    padding: "20px",
    marginBottom: "30px",
    borderRadius: "6px",
    backgroundColor: FEATURE_FLAGS.ENABLE_SENTRY ? "#d4edda" : "#fff3cd",
    border: `2px solid ${FEATURE_FLAGS.ENABLE_SENTRY ? "#28a745" : "#ffc107"}`,
  };

  const buttonStyle = {
    padding: "12px 24px",
    margin: "8px",
    fontSize: "16px",
    fontWeight: "600",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "opacity 0.2s",
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#1976d2",
    color: "white",
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#f44336",
    color: "white",
  };

  const resultStyle = {
    marginTop: "20px",
    padding: "15px",
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    fontSize: "14px",
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  };

  return (
    <div style={containerStyle}>
      <h1 style={{ marginTop: 0, color: "#333" }}>Sentry Error Testing</h1>

      {/* Sentry Status */}
      <div style={statusBoxStyle}>
        <h2 style={{ marginTop: 0, fontSize: "18px", color: "#333" }}>
          Sentry Status: {FEATURE_FLAGS.ENABLE_SENTRY ? "ENABLED" : "DISABLED"}
        </h2>
        <p style={{ margin: "8px 0", fontSize: "14px", color: "#666" }}>
          <strong>Environment:</strong> {process.env.NODE_ENV}
        </p>
        <p style={{ margin: "8px 0", fontSize: "14px", color: "#666" }}>
          <strong>Base URL:</strong> {baseURL.baseURL || "Loading..."}
        </p>
        {FEATURE_FLAGS.ENABLE_SENTRY ? (
          <p style={{ margin: "8px 0 0 0", fontSize: "13px", color: "#28a745" }}>
            ✓ Errors will be reported to Sentry dashboard
          </p>
        ) : (
          <p style={{ margin: "8px 0 0 0", fontSize: "13px", color: "#856404" }}>
            ⚠ Errors will NOT be reported to Sentry (local development mode)
          </p>
        )}
      </div>

      {/* Test Buttons */}
      <div style={{ marginBottom: "20px" }}>
        <h3 style={{ fontSize: "16px", color: "#333" }}>Test Error Reporting:</h3>

        <button
          style={primaryButtonStyle}
          onClick={handleCapturedError}
          disabled={!baseURL || !baseURL.baseURL}
          onMouseOver={(e) => (e.target.style.opacity = "0.9")}
          onMouseOut={(e) => (e.target.style.opacity = "1")}
        >
          Test Captured Exception
        </button>

        <button
          style={secondaryButtonStyle}
          onClick={handleUncaughtError}
          onMouseOver={(e) => (e.target.style.opacity = "0.9")}
          onMouseOut={(e) => (e.target.style.opacity = "1")}
        >
          Test Uncaught Error (Crashes App)
        </button>

        <div style={{ marginTop: "15px", fontSize: "13px", color: "#666" }}>
          <p style={{ margin: "5px 0" }}>
            <strong>Captured Exception:</strong> Manually sends error to Sentry, app continues running
          </p>
          <p style={{ margin: "5px 0" }}>
            <strong>Uncaught Error:</strong> Throws error that crashes React, triggers ErrorBoundary
          </p>
        </div>
      </div>

      {/* Test Result */}
      {lastTestResult && (
        <div style={resultStyle}>
          {lastTestResult}
        </div>
      )}

      {/* Instructions */}
      <div style={{ marginTop: "30px", padding: "15px", backgroundColor: "#e3f2fd", borderRadius: "4px", fontSize: "13px" }}>
        <h4 style={{ marginTop: 0, color: "#1976d2" }}>Testing Instructions:</h4>
        <ol style={{ margin: "8px 0", paddingLeft: "20px", lineHeight: "1.8" }}>
          <li>Check the status box above to confirm Sentry is enabled/disabled as expected</li>
          <li>Click "Test Captured Exception" to manually send an error</li>
          <li>Click "Test Uncaught Error" to trigger an error boundary crash</li>
          <li>Check your Sentry dashboard to verify errors are being reported (when enabled)</li>
        </ol>
        <p style={{ margin: "12px 0 0 0", fontSize: "12px", color: "#666" }}>
          <strong>Expected behavior:</strong><br />
          • <code>npm start</code> (local dev) → Sentry DISABLED<br />
          • <code>npm run build:dev</code> → Sentry ENABLED<br />
          • <code>npm run build:prod</code> → Sentry ENABLED
        </p>
      </div>
    </div>
  );
}
